import { memo, useCallback, useMemo } from 'react'

import { Box, FormControl, InputLabel, MenuItem, OutlinedInput, Select, Slider, TextField, Mark } from '@mui/material'
import { AvcHlsOutputConfigModeEnum, AvcHlsOutputConfigTargetLatencyModeEnum } from '@nativewaves/nw-cloud-sdk/content'
import { Controller, UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { InputErrorEndAdornment } from '@shared/components/ErrorHandling'
import { FormStructure } from 'domains/Workspaces/ContentFlows/AvcTasks/AvcTask/Task/Config/Outputs/Dialogs/Hls'

const marks = [
  {
    value: 5,
    label: 'Custom',
  },
  {
    value: 18,
    label: '~ 30 sec.',
  },
  {
    value: 60,
    label: '< 3 sec.',
  },
  {
    value: 95,
    label: '< 1 sec.',
  },
] satisfies Mark[]

const valueToTargetLatencyModeMap = {
  5: AvcHlsOutputConfigTargetLatencyModeEnum.Custom,
  18: AvcHlsOutputConfigTargetLatencyModeEnum.Standard,
  60: AvcHlsOutputConfigTargetLatencyModeEnum.LowLatency,
  95: AvcHlsOutputConfigTargetLatencyModeEnum.UltraLowLatency,
} as const

// Inverse object to select default value

const targetLatencyModeToLabelsMap = {
  [AvcHlsOutputConfigTargetLatencyModeEnum.Custom]: 'Custom',
  [AvcHlsOutputConfigTargetLatencyModeEnum.Standard]: 'Standard',
  [AvcHlsOutputConfigTargetLatencyModeEnum.LowLatency]: 'Low',
  [AvcHlsOutputConfigTargetLatencyModeEnum.UltraLowLatency]: 'Ultra Low',
} as const

const targetLatencyToModeMap = {
  [AvcHlsOutputConfigTargetLatencyModeEnum.Custom]: AvcHlsOutputConfigModeEnum.Standard,
  [AvcHlsOutputConfigTargetLatencyModeEnum.Standard]: AvcHlsOutputConfigModeEnum.Auto,
  [AvcHlsOutputConfigTargetLatencyModeEnum.LowLatency]: AvcHlsOutputConfigModeEnum.Auto,
  [AvcHlsOutputConfigTargetLatencyModeEnum.UltraLowLatency]: AvcHlsOutputConfigModeEnum.Auto,
} as const

// type LatencyOptions = keyof typeof latencyOptions

type LatencySelectorProps = UseFormReturn<FormStructure>

const LatencySelectorRaw = ({ getValues, setValue, watch, control }: LatencySelectorProps) => {
  const { t } = useTranslation(['domain'])

  const targetLatencyMode = watch('targetLatencyMode')
  const mode = watch('mode')
  const segmentDuration = watch('segmentDuration')

  const hasPartDurationField = useMemo(
    () => targetLatencyMode === 'Custom' && (mode === 'LowLatency' || mode === 'LowLatencyCmaf'),
    [mode, targetLatencyMode],
  )

  const handleChangeLatencyMode = useCallback(
    (value: number) => {
      const newLatencyOption = valueToTargetLatencyModeMap[value as keyof typeof valueToTargetLatencyModeMap]
      setValue('targetLatencyMode', newLatencyOption)
      setValue('mode', targetLatencyToModeMap[newLatencyOption])

      if (newLatencyOption !== 'Custom') {
        setValue(`segmentDuration`, null)
        setValue(`partDuration`, null)
      }
    },
    [setValue],
  )

  return (
    <>
      <Controller
        control={control}
        name="targetLatencyMode"
        render={() => (
          <Box sx={{ gridColumn: '1 / span 3', mx: 0.5 }}>
            <Slider
              onChange={(_, value) => handleChangeLatencyMode(value as number)}
              defaultValue={
                Object.entries(valueToTargetLatencyModeMap).find(
                  ([_, latMode]) => latMode === getValues().targetLatencyMode,
                )?.[0] as unknown as number
              }
              step={null}
              marks={marks}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) =>
                `${targetLatencyModeToLabelsMap[valueToTargetLatencyModeMap[value as keyof typeof valueToTargetLatencyModeMap]]}`
              }
            />
          </Box>
        )}
      />
      <Controller
        control={control}
        name="mode"
        render={({ field }) =>
          targetLatencyMode === 'Custom' ? (
            <FormControl sx={{ gridColumn: 1 }}>
              <InputLabel id="latencyMode">{t('domain:Workspace.AvcTask.mode')}</InputLabel>
              <Select
                {...field}
                labelId="latencyMode"
                input={<OutlinedInput label={t('domain:Workspace.AvcTask.mode')} />}
                onChange={(e) => {
                  field.onChange(e)
                }}
              >
                {Object.entries(AvcHlsOutputConfigModeEnum)
                  .filter(([_, latencyOption]) => latencyOption !== 'Auto')
                  .map(([key, latencyOption]) => (
                    <MenuItem key={key} value={key}>
                      {latencyOption}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          ) : (
            <></>
          )
        }
      />
      <Controller
        control={control}
        name="segmentDuration"
        render={({ field }) =>
          targetLatencyMode === 'Custom' ? (
            <TextField {...field} required type="number" label={t('domain:Workspace.AvcTask.segmentDuration')} />
          ) : (
            <></>
          )
        }
      />
      <Controller
        control={control}
        name="partDuration"
        rules={{
          validate: (value) => {
            if (hasPartDurationField) {
              return !segmentDuration || !value || value >= segmentDuration
                ? t('domain:Workspace.AvcTask.partDurationError')
                : true
            }
          },
        }}
        render={({ field, fieldState }) =>
          hasPartDurationField ? (
            <TextField
              {...field}
              error={fieldState.invalid}
              required
              type="number"
              label={t('domain:Workspace.AvcTask.partDuration')}
              slotProps={{
                input: {
                  endAdornment: <InputErrorEndAdornment fieldState={fieldState} isSubmitted={false} />,
                },
              }}
            />
          ) : (
            <></>
          )
        }
      />
    </>
  )
}

export const LatencySelector = memo(LatencySelectorRaw)
